import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { useSeo } from "@/hooks/useSeo";
import { mediaList } from "@/mocks/media";
import ContactForm from "@/pages/about/components/ContactForm";

const values = [
  {
    icon: "ri-database-2-line",
    title: "统一字段的资料库",
    desc: "海报、演职员、类型、地区、年份、集数等字段统一整理，不同分类之间可直接横向比较。",
  },
  {
    icon: "ri-focus-3-line",
    title: "只做聚合与索引",
    desc: "本站不托管、不转存任何视频文件，只呈现公开的影视资料与索引信息，帮助用户更快了解作品。",
  },
  {
    icon: "ri-heart-3-line",
    title: "轻负担的使用体验",
    desc: "无需注册即可使用全部浏览功能，收藏与浏览记录只保存在你自己的浏览器中。",
  },
];

const terms = [
  "本站展示的影视海报、简介与演职员资料来自公开资料库，版权归原权利人所有。",
  "本站不提供在线播放、下载或任何形式的视频转存服务，剧集目录仅作资料展示用途。",
  "如权利人认为本站展示的资料侵犯其合法权益，可通过下方表单提交反馈，我们会尽快核实处理。",
  "收藏、浏览记录等功能依赖浏览器本地存储，清理浏览器数据后将无法恢复。",
];

const steps = [
  { step: "01", title: "选择分类", desc: "首页或顶部导航进入电影、电视剧、综艺频道" },
  { step: "02", title: "组合筛选", desc: "按类型、地区、年份过滤，并按热度或评分排序" },
  { step: "03", title: "查看详情", desc: "在详情页查看简介、演职员与剧集目录资料" },
  { step: "04", title: "收藏回溯", desc: "加入收藏或依靠浏览记录随时找回作品" },
];

export default function AboutPage() {
  useSeo(
    "关于我们 - 97视频高清影视内容聚合平台",
    "了解97视频的平台定位、内容范围与联系方式。我们专注于电影、电视剧、综艺及影视资讯的资料聚合与索引，不提供在线播放服务。",
    "关于97视频,影视内容聚合,联系方式,服务条款,97视频介绍",
  );

  return (
    <SiteLayout>
      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Minimal%20dark%20cinema%20auditorium%20with%20rows%20of%20empty%20seats%20and%20a%20soft%20warm%20projector%20beam%20from%20behind%2C%20deep%20navy%20and%20amber%20cinematic%20atmosphere%2C%20wide%20architectural%20composition&width=1600&height=700&seq=97v-about-hero&orientation=landscape"
            alt="97视频平台介绍"
            className="h-full w-full object-cover object-top opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/80 to-primary-950/40" />
        </div>
        <div className="relative z-10 w-full px-4 py-14 md:px-6 md:py-20">
          <nav className="flex items-center gap-1.5 font-label text-[11px] text-background-50/50">
            <Link to="/" className="transition-colors hover:text-accent-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line flex h-3.5 w-3.5 items-center justify-center" />
            <span className="text-background-50/80">关于我们</span>
          </nav>
          <p className="mt-12 font-label text-[11px] uppercase tracking-[0.28em] text-accent-400">About 97 Video</p>
          <h1 className="mt-3 max-w-3xl font-heading text-3xl font-black leading-tight tracking-tight text-background-50 md:text-5xl">
            把影视资料整理清楚，
            <br className="hidden md:block" />
            让选择变得简单
          </h1>
          <p className="mt-5 max-w-2xl text-[13px] leading-relaxed text-background-50/70 md:text-sm">
            97视频是一个面向影视爱好者的高清影视视频内容聚合平台。我们把电影、电视剧、综艺与影视资讯重新整理成清晰可查的档案，让你在一分钟内找到想了解的作品。
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/category/movie"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-[13px] font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-circle-line flex h-4 w-4 items-center justify-center" />
              开始浏览影视
            </Link>
            <a
              href="#contact"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-[13px] text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-mail-line flex h-4 w-4 items-center justify-center" />
              联系我们
            </a>
          </div>
        </div>
      </section>

      <div className="w-full px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {[
            { value: `${mediaList.length}`, label: "在库影视条目", unit: "部" },
            { value: `${mediaList.filter((i) => i.type === "tv").length}`, label: "连载剧集", unit: "部" },
            { value: "4", label: "覆盖地区", unit: "个" },
            { value: "100%", label: "免费浏览", unit: "" },
          ].map((stat) => (
            <div key={stat.label} className="rounded-lg border border-background-200 bg-background-100/60 px-4 py-5">
              <p className="font-heading text-2xl font-bold text-primary-700">
                {stat.value}
                <span className="ml-0.5 font-label text-[11px] font-normal text-foreground-500">{stat.unit}</span>
              </p>
              <p className="mt-1.5 font-label text-[11px] text-foreground-500">{stat.label}</p>
            </div>
          ))}
        </div>

        <section className="mt-12">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">我们坚持的三件事</h2>
          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
            {values.map((value) => (
              <div key={value.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${value.icon} text-lg`} />
                </span>
                <h3 className="mt-4 font-heading text-sm font-semibold text-foreground-950">{value.title}</h3>
                <p className="mt-2 text-[12px] leading-relaxed text-foreground-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="usage" className="mt-12 scroll-mt-24 rounded-lg bg-primary-500 p-5 md:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-sm">
              <p className="font-label text-[11px] uppercase tracking-[0.22em] text-accent-400">How to use</p>
              <h2 className="mt-2 font-heading text-lg font-bold text-background-50 md:text-xl">四步找到想看的影视</h2>
              <p className="mt-2 text-[12px] leading-relaxed text-background-50/65">
                全站功能无需登录即可使用，收藏与浏览记录保存在当前浏览器本地。
              </p>
            </div>
            <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:max-w-2xl">
              {steps.map((step) => (
                <div key={step.step} className="rounded-md border border-background-50/15 bg-background-50/5 p-4">
                  <span className="font-label text-[11px] font-bold text-accent-400">{step.step}</span>
                  <h3 className="mt-1.5 font-heading text-[13px] font-semibold text-background-50">{step.title}</h3>
                  <p className="mt-1 text-[11px] leading-relaxed text-background-50/60">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-5 lg:gap-8">
          <section id="contact" className="scroll-mt-24 lg:col-span-3">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">留言与反馈</h2>
            <p className="mt-2 text-[13px] leading-relaxed text-foreground-600">
              发现资料错误、想要补充作品，或希望与我们合作？填写下面的表单，我们会尽快回复。
            </p>
            <div className="mt-5">
              <ContactForm />
            </div>
          </section>

          <aside className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">联系方式</h2>
              <ul className="mt-4 flex flex-col gap-4">
                {[
                  { icon: "ri-mail-line", label: "反馈邮箱", value: "hello@97video.example" },
                  { icon: "ri-time-line", label: "回复时间", value: "工作日 10:00 - 18:00" },
                  { icon: "ri-map-pin-line", label: "内容团队", value: "线上协作 · 不接受来访" },
                ].map((row) => (
                  <li key={row.label} className="flex items-start gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-50 text-accent-600">
                      <i className={`${row.icon} text-sm`} />
                    </span>
                    <div>
                      <p className="font-label text-[10px] uppercase tracking-[0.14em] text-foreground-400">
                        {row.label}
                      </p>
                      <p className="mt-0.5 text-[12px] text-foreground-900">{row.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-5 rounded-md bg-background-50 p-3">
                <p className="text-[11px] leading-relaxed text-foreground-500">
                  本站不提供影片播放与下载服务。如需举报侵权内容，请在留言类型中选择“版权反馈”，并附上作品名称。
                </p>
              </div>
            </div>
          </aside>
        </div>

        <section id="terms" className="mt-12 scroll-mt-24">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">使用说明与服务条款</h2>
          <ol className="mt-5 flex flex-col gap-3">
            {terms.map((term, index) => (
              <li
                key={term}
                className="flex gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3.5"
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm bg-secondary-100 font-label text-[10px] font-semibold text-secondary-800">
                  {index + 1}
                </span>
                <p className="text-[12px] leading-relaxed text-foreground-700">{term}</p>
              </li>
            ))}
          </ol>
        </section>

        <div className="mt-12 flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100/60 px-6 py-10 text-center">
          <h2 className="font-heading text-lg font-bold text-foreground-950">看完了，去挑一部吧</h2>
          <p className="text-[12px] text-foreground-600">从最新更新的作品开始，或者直接搜索你想看的关键词</p>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-[13px] font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-home-4-line flex h-4 w-4 items-center justify-center" />
              返回首页
            </Link>
            <Link
              to="/search"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-[13px] text-foreground-800 transition-colors hover:border-accent-300 hover:text-accent-600"
            >
              <i className="ri-search-line flex h-4 w-4 items-center justify-center" />
              搜索影视
            </Link>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}